import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

type Labels = Record<string, string>;
type Form = "singular" | "plural";

const DEFAULTS: Labels = {
  contact: "Contact",
  organization: "Organization",
  opportunity: "Opportunity",
  interaction: "Interaction",
  visit: "Visit",
  conversion: "Conversion",
};

function singularize(value: string) {
  if (/ies$/i.test(value)) return `${value.slice(0, -3)}y`;
  if (/sses$/i.test(value)) return value.slice(0, -2);
  if (/s$/i.test(value) && !/ss$/i.test(value)) return value.slice(0, -1);
  return value;
}

function pluralize(value: string) {
  if (/[^aeiou]y$/i.test(value)) return `${value.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(value)) return `${value}es`;
  return `${value}s`;
}

export function displayForm(value: string, form: Form) {
  const singular = singularize(value.trim());
  return form === "plural" ? pluralize(singular) : singular;
}

type TerminologyValue = {
  labels: Labels;
  term: (code: string, form?: Form) => string;
  reload: () => Promise<void>;
};

const TerminologyContext = createContext<TerminologyValue>({
  labels: DEFAULTS,
  term: (code, form = "singular") => displayForm(DEFAULTS[code] || code, form),
  reload: async () => undefined,
});

export function TerminologyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [labels, setLabels] = useState<Labels>(DEFAULTS);

  const reload = useCallback(async () => {
    if (!user || user.scope === "platform") {
      setLabels(DEFAULTS);
      return;
    }
    try {
      const { data } = await api.get<{ labels: Labels }>("/tenant/terminology");
      setLabels({ ...DEFAULTS, ...(data.labels || {}) });
    } catch {
      setLabels(DEFAULTS);
    }
  }, [user]);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    const handleSaved = () => { void reload(); };
    window.addEventListener("remedikeep:terminology-saved", handleSaved);
    return () => window.removeEventListener("remedikeep:terminology-saved", handleSaved);
  }, [reload]);

  const value = useMemo<TerminologyValue>(() => ({
    labels,
    term: (code, form = "singular") => displayForm(labels[code] || DEFAULTS[code] || code, form),
    reload,
  }), [labels, reload]);

  return <TerminologyContext.Provider value={value}>{children}</TerminologyContext.Provider>;
}

export const useTerminology = () => useContext(TerminologyContext);
